import { Box, CircularProgress, Grid } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { IPost } from '_interfaces/post.interface';
import { IPostComment } from '_interfaces/postComment.interface';
import { IProjectProfiles } from 'redux-store/api/projects';
import { allUserDraftsAtom, editableDraftPostAtom, selectedProjectAtom } from 'atoms';
import { IPostStatus } from '_interfaces/types/post.types';
import { ICustomMentionData } from 'components/inputs/draft-js-input';
import { useAtomValue, useSetAtom } from 'jotai';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetMeQuery } from 'redux-store/api/auth';
import { useAppSelector } from 'redux-store/hooks/useAppSelector';
import { authSelector } from 'redux-store/selector';
import { SIDE_PANEL_WIDTH } from 'utils/constants';
import ActivityFeedHeader from './components/header';
import CommentLikersListModal from './components/comments/components/comment-likers';
import PostLikersListModal from './components/likers';
import Post from './components/post';
import UserPost from './components/post-items/user-post/UserPost';
import { SystemPostItem } from './components/post-items/system-post/SystemPostItem';
import { ContentWrapper } from './helpers/content-wrapper';
import { styles } from './styles/styles';
import { NavigationHandlers } from 'utils/systemPostHelpers';

const PADDING_X = 20;
const SCROLL_DEBOUNCE_MS = 1000;

export interface IActivityFeedProps {
  posts: any;
  mentionOptions: ICustomMentionData[];
  searchQuery: string;
  uploadPath?: string;
  isLoading?: boolean;
  skeletonComponent?: React.ReactNode;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onCreatePost?: (body: Partial<IPost>) => Promise<void> | void;
  onCreateComment?: (body: Partial<IPostComment>, parentPath: string) => Promise<void> | void;
  onUpdatePost?: (body: Partial<IPost>) => Promise<void> | void;
  onUpdateComment?: (body: Partial<IPostComment>) => Promise<void> | void;
  onDeletePost?: (id: string) => void;
  onDeleteComment?: (id: string) => void;
  onTogglePostLike?: (id: string) => void;
  onSetSearchQuery: (query: string) => void;
  onClose?: () => void;
  resetFilters?: () => void;
  navigationHandlers?: NavigationHandlers;
  mentionedUser?: IProjectProfiles | null;
  editorKey?: number;
  isEditorExpanded?: boolean;
  onEditorExpandChange?: (expanded: boolean) => void;
  onResetMentionedProfile?: () => void;
  isInModal?: boolean;
  feedContextType?: 'ACTIVITY_FEED' | 'FILE_FEED' | 'SCHEDULE_FEED' | 'SELECTIONS_FEED';
  feedContextItemId?: string;
}

const ActivityFeed: FC<IActivityFeedProps> = ({
  posts,
  mentionOptions,
  searchQuery,
  uploadPath,
  isLoading = false,
  skeletonComponent,
  hasMore = true,
  onLoadMore,
  onCreatePost,
  onCreateComment,
  onUpdatePost,
  onUpdateComment,
  onDeletePost,
  onDeleteComment,
  onTogglePostLike,
  onSetSearchQuery,
  onClose,
  resetFilters,
  navigationHandlers,
  mentionedUser,
  editorKey,
  isEditorExpanded,
  onEditorExpandChange,
  onResetMentionedProfile,
  isInModal = false,
  feedContextType = 'ACTIVITY_FEED',
  feedContextItemId,
}) => {
  const { companyId } = useAppSelector(authSelector);
  const selectedProject = useAtomValue(selectedProjectAtom);
  const { data: userMe } = useGetMeQuery();

  const editableDraft = useAtomValue(editableDraftPostAtom);
  const setEditableDraft = useSetAtom(editableDraftPostAtom);
  const setDrafts = useSetAtom(allUserDraftsAtom);

  const parentRef = useRef<HTMLDivElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(isEditorExpanded ?? false);
  const isLoadingRef = useRef(false);

  // Auto-expand composer when a draft is set for completion
  useEffect(() => {
    if (editableDraft) setIsExpanded(true);
  }, [editableDraft]);

  // Clear editable draft on unmount so Composer is clean on re-entry
  useEffect(() => {
    return () => {
      setEditableDraft(null);
    };
  }, [setEditableDraft]);

  // Expand Composer when message icon tags a user
  useEffect(() => {
    if (mentionedUser) setIsExpanded(true);
  }, [mentionedUser]);

  const getItemKey = useCallback((index: number) => posts?.[index]?.id || index, [posts]);
  const virtualizer = useVirtualizer({
    count: posts?.length || 0,
    estimateSize: () => 300,
    getScrollElement: () => parentRef.current,
    getItemKey,
    overscan: 25,
  });

  const items = virtualizer.getVirtualItems();

  useEffect(() => {
    const container = parentRef.current;
    if (!container || !onLoadMore) return;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const handleScroll = () => {
      if (!hasMore || isLoadingRef.current || searchQuery.trim()) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 300) {
        isLoadingRef.current = true;
        onLoadMore();
        timeoutId = setTimeout(() => {
          isLoadingRef.current = false;
        }, SCROLL_DEBOUNCE_MS);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [hasMore, onLoadMore, searchQuery]);

  const isEmpty = !posts?.length;

  const handleExpand = useCallback(
    (expanded: boolean) => {
      setIsExpanded(expanded);
      onEditorExpandChange?.(expanded);
    },
    [onEditorExpandChange]
  );

  const handleSetOpen = useCallback(
    (open: boolean) => {
      setIsExpanded(open);
      onEditorExpandChange?.(open);
    },
    [onEditorExpandChange]
  );

  const handleCreatePostForDraft = useCallback(
    async (body: Partial<IPost>) => {
      if (editableDraft) {
        await onUpdatePost?.({
          ...body,
          id: editableDraft.id,
          status: IPostStatus.ACTIVE,
          savedAsDraft: false,
        });
        setDrafts((prev) => prev.filter((d) => d.id !== editableDraft.id));
        setEditableDraft(null);
      } else {
        await onCreatePost?.(body);
      }
    },
    [editableDraft, onCreatePost, onUpdatePost, setEditableDraft, setDrafts]
  );

  if (isLoading && skeletonComponent) {
    return (
      <Grid sx={styles.container}>
        <ActivityFeedHeader
          onSetSearchQuery={onSetSearchQuery}
          onClose={onClose}
          resetFilters={resetFilters}
        />
        <Grid sx={{ ...styles.mainContainer }}>{skeletonComponent}</Grid>
      </Grid>
    );
  }

  if (!selectedProject) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px',
          width: SIDE_PANEL_WIDTH,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid sx={{ ...styles.container, ...(isInModal ? { width: '100%' } : {}) }}>
      <ActivityFeedHeader
        onSetSearchQuery={onSetSearchQuery}
        onClose={onClose}
        resetFilters={resetFilters}
      />

      <Grid
        ref={parentRef}
        sx={{
          ...styles.mainContainer,
          scrollBehavior: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <ContentWrapper isEmpty={isEmpty} isError={false} isLoading={false}>
          <Grid
            sx={{
              width: `calc(100% - ${2 * PADDING_X - 5}px)`,
              height: virtualizer.getTotalSize(),
              position: 'relative',
            }}
          >
            <Grid
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${items[0]?.start ?? 0}px)`,
              }}
            >
              {items.map(({ index, key }) => {
                const isFirst = index === 0;
                const post = posts?.[index];
                if (!post) return null;

                return (
                  <Grid
                    key={key}
                    data-index={index}
                    ref={virtualizer.measureElement}
                    sx={{
                      display: 'flex',
                      width: '100%',
                      paddingBottom: '20px',
                      borderBottom: '1px solid #EDEFF7',
                      marginTop: isFirst ? 0 : '20px',
                    }}
                  >
                    {post.isSystemPost && post.isSystemPost() ? (
                      <SystemPostItem post={post} navigationHandlers={navigationHandlers} />
                    ) : (
                      <UserPost
                        projectId={selectedProject.id}
                        post={post}
                        placeOfUse="activity_feed"
                        mentionOptions={mentionOptions}
                        searchQuery={searchQuery}
                        onCreateComment={(
                          body: Partial<IPostComment>,
                          parentPath: string = `${post.parentPath}/${post.id}`
                        ) => onCreateComment(body, parentPath)}
                        onUpdateComment={onUpdateComment}
                        onDeletePost={onDeletePost}
                        onUpdatePost={onUpdatePost}
                        onDeleteComment={onDeleteComment}
                        onTogglePostLike={() => onTogglePostLike(post.id)}
                        feedContextType={feedContextType}
                        feedContextItemId={feedContextItemId}
                      />
                    )}
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </ContentWrapper>
      </Grid>

      {companyId && userMe && (
        <Grid sx={styles.createPostContainer}>
          <Post
            key={editableDraft?.id || editorKey}
            isExpanded={isExpanded}
            projectId={selectedProject.id}
            companyId={companyId}
            actionType="CREATE_POST"
            editablePost={editableDraft || undefined}
            mentionedUser={mentionedUser || undefined}
            mentionOptions={mentionOptions}
            onExpand={handleExpand}
            onCreatePost={handleCreatePostForDraft}
            onUpdatePost={onUpdatePost}
            onResetMentionedProfile={onResetMentionedProfile}
            setOpen={handleSetOpen}
            uploadPath={uploadPath}
            isInModal={isInModal}
            feedContextType={feedContextType}
            feedContextItemId={feedContextItemId}
          />
        </Grid>
      )}
      <PostLikersListModal />
      <CommentLikersListModal />
    </Grid>
  );
};

export default ActivityFeed;
